// An Admin should be able to configure a tables columns containing copies of dynamic components
// The supported components are:
// - DatePickerComponent
// - DisplayTextComponent
// - FacilitiesComponent
// - ICD10Component
// - MultiSelectionComponent
// - PMBComponent
// - SingleSelectionComponent
// The end user will be able to add, edit, and delete rows in the table.

import { useEffect, useState } from "react";
import DatePickerComponent from "./DatePickerComponent";
import DisplayTextComponent from "./DisplayTextComponent";
import FacilitiesComponent from "./FacilitiesComponent";
import ICD10Component from "./ICD10Component";
import MultiSelectionComponent from "./MultiSelectionComponent";
import PMBComponent from "./PMBComponent";
import SingleSelectionComponent from "./SingleSelectionComponent";

// Example column config (admin can configure this)
const defaultColumns = [
    { key: "date", label: "Date", type: "DatePickerComponent" },
    { key: "text", label: "Text", type: "DisplayTextComponent" },
    { key: "facility", label: "Facility", type: "FacilitiesComponent" },
    { key: "icd10", label: "ICD10", type: "ICD10Component" },
    { key: "multi", label: "Multi", type: "MultiSelectionComponent" },
    { key: "pmb", label: "PMB", type: "PMBComponent" },
    { key: "single", label: "Single", type: "SingleSelectionComponent" },
];

// Map type to component
const componentMap = {
    DatePickerComponent,
    DisplayTextComponent,
    FacilitiesComponent,
    ICD10Component,
    MultiSelectionComponent,
    PMBComponent,
    SingleSelectionComponent,
};

export default function DynamicComponentsTable({
    component,
    onUserInput,
    onUserInputComplete,
    defaultValues,
    showStatus,
    readonly = false
}) {
    // Initialize rows from defaultValues if available, otherwise empty array
    const [rows, setRows] = useState(() => {
        if (Array.isArray(defaultValues) && defaultValues.length > 0) {
            return defaultValues;
        }
        return [];
    });
    console.log(component)

    // Add responsive CSS for mobile
    useEffect(() => {
        const styleId = 'dynamic-table-responsive-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .dynamic-table-responsive {
                    width: 100%;
                    overflow-x: auto;
                }
                .dynamic-table {
                    min-width: 600px;
                    width: 100%;
                }
                @media (max-width: 768px) {
                    .dynamic-table {
                        display: block;
                        width: 100%;
                        border: none;
                    }
                    .dynamic-table thead {
                        display: none;
                    }
                    .dynamic-table tbody {
                        display: block;
                    }
                    .dynamic-table tbody tr {
                        display: block;
                        margin-bottom: 1rem;
                        border: 1px solid #ddd;
                        border-radius: 8px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        background: #fff;
                        padding: 0.5rem;
                    }
                    .dynamic-table tbody td {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        padding: 0.5rem 0;
                        border: none;
                        border-bottom: 1px solid #eee;
                    }
                    .dynamic-table tbody td:last-child {
                        border-bottom: none;
                    }
                    .dynamic-table tbody td:before {
                        content: attr(data-label);
                        font-weight: bold;
                        margin-bottom: 0.25rem;
                        color: #555;
                        font-size: 0.9em;
                    }
                    .dynamic-table tbody td > * {
                        width: 100%;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Get columns from component.properties if available, else fallback to defaultColumns
    const columns = Array.isArray(component?.properties)
        ? component.properties
            .filter(p => p.property === "Options" && p.values)
            .flatMap(p => {
                try {
                    const parsed = JSON.parse(p.values);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    return [];
                }
            })
        : defaultColumns;

    // Add new row
    const handleAddRow = () => {
        const newRow = {};
        columns.forEach(col => newRow[col.key] = null);
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        if (onUserInput) onUserInput(updatedRows);
    };

    // Edit cell
    const handleCellChange = (rowIdx, colKey, value) => {
        const updatedRows = rows.map((row, idx) =>
            idx === rowIdx ? { ...row, [colKey]: value } : row
        );
        setRows(updatedRows);
        if (onUserInput) onUserInput(updatedRows);
    };

    // Delete row
    const handleDeleteRow = (rowIdx) => {
        const updatedRows = rows.filter((_, idx) => idx !== rowIdx);
        setRows(updatedRows);
        if (onUserInput) onUserInput(updatedRows);
    };

    return (
        <div className="dynamic-table-responsive">
            {!readonly && <button style={{ top: '10px', position: 'sticky', zIndex: 30 }} className="btn btn-primary mt-2" onClick={handleAddRow}>Add Row</button>}
            <table className="table table-collapsed table-sm dynamic-table">
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th key={col.key}>{col.label}</th>
                        ))}
                        {!readonly && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                            {columns.map(col => {
                                const Component = componentMap[col.type];
                                return (
                                    <td key={col.key} data-label={col.label}>
                                        {Component ? (
                                            <Component
                                                defaultValues={row[col.key]}
                                                onUserInput={val => !readonly && handleCellChange(rowIdx, col.key, val)}
                                                readonly={readonly}
                                            />
                                        ) : (
                                            <span>Unsupported</span>
                                        )}
                                    </td>
                                );
                            })}
                            {!readonly && (
                                <td data-label="Actions">
                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRow(rowIdx)}>Delete</button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

        </div>
    );
}